// Page copy for the redesigned Studio. Keep enUS and zhCN structurally identical.
export const enUS = {
  title: 'Usage',
  views: 'Usage views',
  tabs: {
    credits: 'Credits',
  },
  dateRange: {
    label: 'Date range',
    today: 'Today',
    days: '{count}D',
    year: '1Y',
  },
  interval: {
    hourly: 'Hourly',
    daily: 'Daily',
    every3: 'Every 3 days',
    every7: 'Every 7 days',
    monthly: 'Monthly',
  },
  utc: 'UTC',
  intervalLabel: 'Statistics interval',
  regionLabel: 'Region',
  region: { all: 'All regions', bj: 'Beijing', tyo: 'Tokyo', sv: 'Silicon Valley' },
  app: {
    label: 'App',
    all: 'All apps',
    deleted: '{id} (deleted)',
    deletedName: 'Deleted app',
    unknownName: 'Unknown app',
  },
  state: {
    loadingTitle: 'Loading usage…',
    loadingBody: 'Fetching your usage and connection records',
    errorTitle: 'Could not load usage',
    errorBody: 'Your usage could not be retrieved — try again',
  },
  metrics: {
    totalTime: 'Total connection time',
    totalTimeHint: 'Selected time window and region',
    totalTimeDetails: 'Total connection time in the selected time window and region.',
    reported: 'Reported connections',
    reportedHint: 'Sum of reporting interval peaks',
    peak: 'Peak connections',
    peakHint: 'Maximum simultaneous connections',
    peakDetails: 'Maximum simultaneous connection count in the selected time window and region.',
    live: 'Active connections',
    liveHint: 'Current snapshot · All regions',
    liveDetails: 'Current real-time connection count for this account across all regions, unaffected by time-window or region filters.',
    liveRefreshing: 'Refreshing…',
    liveUnavailable: 'Snapshot unavailable · try again',
    refreshLive: 'Refresh active connections',
  },
  charts: {
    duration: 'Connection duration',
    durationCaption: '{interval} · All apps',
    count: 'Peak connections',
    peakLabel: 'Peak in period',
    countCaption: '{interval} · Reporting interval peaks',
    minutes: 'min',
    connections: 'connections',
    totalLabel: 'Total in period',
    empty: 'No data for this period',
    emptyHint: 'Start a session to see your usage here',
    viewData: 'View data',
    period: 'Period (UTC)',
    open: 'Open {title} in Usage',
    ariaLabel: '{title}, in {unit}',
  },
  emptyActions: {
    integrate: 'View integration guide',
  },
  sessions: {
    title: 'Session history',
    records: '{count} records · App filter applies to this history',
    loading: 'Loading connection records…',
    emptyTitle: 'No connections for this selection',
    emptyBody: 'Try another app or date range',
    caption: 'Connection records. All times are UTC. Sorting applies to the current page.',
    columns: {
      connectedAt: 'Connected at',
      disconnectedAt: 'Disconnected at',
      duration: 'Duration',
      app: 'App / Connection ID',
      avatar: 'Avatar ID',
      region: 'Region',
      connectedAtUtc: 'Connected at (UTC)',
      disconnectedAtUtc: 'Disconnected at (UTC)',
      appId: 'App ID',
      connectionId: 'Connection ID',
    },
    active: 'Active',
    perPage: '{count} per page · Sorts the current page',
    prev: 'Previous page',
    next: 'Next page',
  },
  ledger: {
    loading: 'Loading account activity…',
    errorTitle: 'Could not load account activity',
    errorBody: 'Your balance and records will appear when they are available.',
    emptyTitle: 'No transactions for this selection',
    footer: '{loaded} of {total} transactions · All time',
    loadMore: 'Load more',
    type: 'Type',
    allTypes: 'All types',
    balanceAfter: 'Balance after',
    date: 'Date (UTC)',
    typeReference: 'Type / Reference',
  },
  credits: {
    heading: 'Credits remaining',
    description: 'Your current balance, including confirmed account changes.',
    manage: 'Manage credits',
    transactions: 'Credit transactions',
    emptyBody: 'Try another type.',
    caption: 'Credit transactions, newest first',
    columns: {
      description: 'Description',
      credits: 'Credits',
    },
    types: {
      subscriptionAllocation: 'Subscription allocation',
      upgradeReset: 'Upgrade reset',
      manualGrant: 'Manual grant',
      consumption: 'Consumption',
      refund: 'Refund',
      adjustment: 'Adjustment',
      autoReload: 'Auto-reload',
      manualTopup: 'Manual top-up',
      other: 'Other',
    },
  },
  creations: {
    label: 'Avatar Creations usage',
    heading: 'Avatar Creations available',
    description: 'Avatar Creations are separate from usage credits.',
    expiring: '{count} expires within 7 days | {count} expire within 7 days',
    viewPlans: 'View creation plans',
    view: 'View',
    expiryView: 'Expiry details',
    transactionsView: 'Transaction history',
    source: 'Source',
    allSources: 'All sources',
    grantsCaption: 'Avatar Creation grants and expiry details',
    transactionsCaption: 'Avatar Creation transactions, newest first',
    columns: {
      source: 'Source',
      remaining: 'Remaining / Granted',
      validity: 'Validity',
      grantedAt: 'Granted at (UTC)',
      creations: 'Creations',
    },
    emptyGrantsTitle: 'No creation grants yet',
    emptyGrantsBody: 'Your granted Avatar Creations and expiry dates will appear here.',
    emptyTransactionsBody: 'Try another type or source.',
    errorTitle: 'Account activity is not available',
    other: 'Other',
  },
}

export const zhCN = {
  title: '用量',
  views: '用量视图',
  tabs: {
    credits: 'Credits',
  },
  dateRange: {
    label: '日期范围',
    today: '今天',
    days: '{count}天',
    year: '1年',
  },
  interval: {
    hourly: '按小时',
    daily: '按天',
    every3: '每 3 天',
    every7: '每 7 天',
    monthly: '按月',
  },
  utc: 'UTC',
  intervalLabel: '统计粒度',
  regionLabel: '区域',
  region: { all: '全部区域', bj: '北京', tyo: '东京', sv: '硅谷' },
  app: {
    label: '应用',
    all: '全部应用',
    deleted: '{id}（已删除）',
    deletedName: '已删除的应用',
    unknownName: '未知应用',
  },
  state: {
    loadingTitle: '正在加载用量…',
    loadingBody: '正在获取你的用量和连接记录',
    errorTitle: '无法加载用量',
    errorBody: '未能获取你的用量，请重试',
  },
  metrics: {
    totalTime: '总连接时长',
    totalTimeHint: '所选时间窗及区域内',
    totalTimeDetails: '在所选时间窗及区域内，连接时长之和。',
    reported: '上报连接数',
    reportedHint: '各上报周期峰值之和',
    peak: '峰值连接数',
    peakHint: '最大同时连接数',
    peakDetails: '在所选时间窗及区域内，最大的同时连接数量。',
    live: '活跃连接数',
    liveHint: '当前快照 · 全部区域',
    liveDetails: '当前账号所有区域内的实时连接数量，不受时间窗及区域筛选影响。',
    liveRefreshing: '刷新中…',
    liveUnavailable: '快照不可用，请重试',
    refreshLive: '刷新活跃连接数',
  },
  charts: {
    duration: '连接时长',
    durationCaption: '{interval} · 全部应用',
    count: '峰值连接数',
    peakLabel: '时间段峰值',
    countCaption: '{interval} · 上报周期峰值',
    minutes: '分钟',
    connections: '连接',
    totalLabel: '时间段合计',
    empty: '该时间段暂无数据',
    emptyHint: '发起一次会话后即可在这里查看用量',
    viewData: '查看数据',
    period: '周期（UTC）',
    open: '在用量页打开{title}',
    ariaLabel: '{title}，单位 {unit}',
  },
  emptyActions: {
    integrate: '查看接入指南',
  },
  sessions: {
    title: '会话记录',
    records: '{count} 条记录 · 应用筛选仅作用于此记录',
    loading: '正在加载连接记录…',
    emptyTitle: '当前筛选下没有连接',
    emptyBody: '换一个应用或日期范围试试',
    caption: '连接记录。时间均为 UTC，排序仅作用于当前页。',
    columns: {
      connectedAt: '连接时间',
      disconnectedAt: '断开时间',
      duration: '时长',
      app: '应用 / 连接 ID',
      avatar: '人物 ID',
      region: '区域',
      connectedAtUtc: '连接时间（UTC）',
      disconnectedAtUtc: '断开时间（UTC）',
      appId: '应用 ID',
      connectionId: '连接 ID',
    },
    active: '进行中',
    perPage: '每页 {count} 条 · 仅排序当前页',
    prev: '上一页',
    next: '下一页',
  },
  ledger: {
    loading: '正在加载账户流水…',
    errorTitle: '无法加载账户流水',
    errorBody: '余额和记录可用后会显示在这里。',
    emptyTitle: '当前筛选下没有流水',
    footer: '已显示 {loaded} / {total} 条 · 全部时间',
    loadMore: '加载更多',
    type: '类型',
    allTypes: '全部类型',
    balanceAfter: '变动后余额',
    date: '日期（UTC）',
    typeReference: '类型 / 引用',
  },
  credits: {
    heading: '剩余 Credits',
    description: '当前余额，已包含确认入账的账户变动。',
    manage: '管理 Credits',
    transactions: 'Credits 流水',
    emptyBody: '换一个类型试试。',
    caption: 'Credits 流水，最新在前',
    columns: {
      description: '说明',
      credits: 'Credits',
    },
    types: {
      subscriptionAllocation: '订阅分配',
      upgradeReset: '升级重置',
      manualGrant: '手动赠送',
      consumption: '消费',
      refund: '退款',
      adjustment: '调整',
      autoReload: '自动充值',
      manualTopup: '手动充值',
      other: '其他',
    },
  },
  creations: {
    label: 'Avatar Creations 用量',
    heading: '可用 Avatar Creations',
    description: 'Avatar Creations 与用量 Credits 分开计算。',
    expiring: '{count} 次将在 7 天内过期',
    viewPlans: '查看创建方案',
    view: '视图',
    expiryView: '有效期明细',
    transactionsView: '流水记录',
    source: '来源',
    allSources: '全部来源',
    grantsCaption: 'Avatar Creation 发放批次与有效期明细',
    transactionsCaption: 'Avatar Creation 流水，最新在前',
    columns: {
      source: '来源',
      remaining: '剩余 / 发放',
      validity: '有效期',
      grantedAt: '发放时间（UTC）',
      creations: '次数',
    },
    emptyGrantsTitle: '暂无发放记录',
    emptyGrantsBody: '获得的 Avatar Creations 及过期时间会显示在这里。',
    emptyTransactionsBody: '换一个类型或来源试试。',
    errorTitle: '账户流水不可用',
    other: '其他',
  },
}
